import { HIPAATIZER_ORIGINS } from './hipaatizer.js';
import { HIPAATIZER_UTM_KEYS, sanitizeHipaatizerAttribution } from './hipaatizerAttribution.js';

let controllerSequence = 0;
const keyOf = values => JSON.stringify(HIPAATIZER_UTM_KEYS.map(key => values[key] || ''));
const populated = values => Object.keys(values).length > 0;

// Controls only four hidden attribution fields. Never reads form answers or
// forwards response/event payloads to the caller.
export function createHipaatizerAttributionController({
  getFrameWindow,
  formOrigin,
  readValues,
  initialValues = {},
  onStatus,
  timeoutMs = 3000,
  makeRequestId,
  setTimeoutFn = setTimeout,
  clearTimeoutFn = clearTimeout,
}) {
  const sequence = ++controllerSequence;
  let requestSequence = 0;
  const requestId = () => makeRequestId ? makeRequestId()
    : `tda-utm-${Date.now()}-${sequence}-${++requestSequence}`;
  const seeded = sanitizeHipaatizerAttribution(initialValues);
  let desired = { values: seeded, key: keyOf(seeded), generation: 0 };
  let possiblyPopulated = populated(seeded);
  let ready = false;
  let disposed = false;
  let flight = null;
  let attemptedGeneration = -1;
  let confirmedGeneration = -1;
  let status = 'idle';

  function setStatus(next) {
    if (!disposed && status !== next) {
      status = next;
      onStatus?.(next);
    }
  }

  function pump() {
    if (disposed) return;
    if (!ready) {
      setStatus(populated(desired.values) || possiblyPopulated ? 'waiting' : 'idle');
      return;
    }
    if (flight) return;
    if (confirmedGeneration === desired.generation) {
      setStatus(populated(desired.values) ? 'applied' : 'cleared');
      return;
    }
    if (!populated(desired.values) && !possiblyPopulated) {
      setStatus('idle');
      return;
    }
    // A repeated ready event is not an unbounded retry after a failure/timeout.
    if (attemptedGeneration === desired.generation) return;
    const frame = getFrameWindow();
    if (!frame || !HIPAATIZER_ORIGINS.includes(formOrigin)) {
      attemptedGeneration = desired.generation;
      setStatus('error');
      return;
    }
    const request = {
      id: requestId(), frame, generation: desired.generation,
      values: desired.values, key: desired.key, timer: null,
    };
    flight = request;
    attemptedGeneration = request.generation;
    // A sent command may take effect even when its ACK never reaches us.
    if (populated(request.values)) possiblyPopulated = true;
    request.timer = setTimeoutFn(() => {
      if (disposed || flight !== request) return;
      flight = null;
      setStatus('timeout');
      if (desired.generation !== request.generation) pump();
    }, timeoutMs);
    setStatus('pending');
    try {
      frame.postMessage({
        kind: 'request',
        requestId: request.id,
        command: {
          type: 'SetFieldsValue',
          // Include empty strings to clear fields absent from the latest state.
          payload: { values: Object.fromEntries(HIPAATIZER_UTM_KEYS.map(key => [key, request.values[key] || ''])) },
        },
      }, formOrigin);
    } catch {
      if (flight === request) {
        clearTimeoutFn(request.timer);
        flight = null;
        setStatus('error');
        if (desired.generation !== request.generation) pump();
      }
    }
  }

  function refresh() {
    if (disposed) return;
    let values;
    try { values = sanitizeHipaatizerAttribution(readValues()); }
    catch { values = {}; }
    const key = keyOf(values);
    if (key !== desired.key) {
      desired = { values, key, generation: desired.generation + 1 };
    }
    pump();
  }

  function handleMessage(event) {
    if (disposed || !formOrigin || !HIPAATIZER_ORIGINS.includes(formOrigin)) return false;
    const frame = getFrameWindow();
    if (!frame || event.origin !== formOrigin || event.source !== frame) return false;
    const message = event.data;
    if (!message || typeof message !== 'object' || Array.isArray(message)) return false;
    if (message.kind === 'event' && message.type === 'FormReady') {
      ready = true;
      refresh();
      return true;
    }
    if (message.kind !== 'response' || !flight ||
        typeof message.requestId !== 'string' || message.requestId !== flight.id ||
        event.source !== flight.frame) return false;
    const completed = flight;
    clearTimeoutFn(completed.timer);
    flight = null;
    // SetFieldsValue returns no data; a valid success may omit it or use null.
    const success = message.success === true &&
      (message.data === undefined || message.data === null ||
        (typeof message.data === 'object' && !Array.isArray(message.data))) &&
      (message.error === undefined || message.error === null || message.error === '');
    if (success) {
      confirmedGeneration = completed.generation;
      possiblyPopulated = populated(completed.values);
      if (completed.generation === desired.generation) {
        setStatus(populated(completed.values) ? 'applied' : 'cleared');
      }
    } else {
      setStatus('error');
    }
    if (completed.generation !== desired.generation) pump();
    return true;
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    if (flight) clearTimeoutFn(flight.timer);
    flight = null;
    ready = false;
    status = 'disposed';
  }

  return { refresh, handleMessage, dispose, getStatus: () => status };
}
